import type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import ArrowLeftIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowRightIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import RotateLeftIcon from "@mui/icons-material/RotateLeftSharp";
import RotateRightIcon from "@mui/icons-material/RotateRightSharp";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { createFileRoute } from "@tanstack/react-router";

import type { Table } from "~/providers/table-provider";
import { FloorPlanItem } from "~/components/floor-plan/floor-plan-item";
import { FloorPlanMapView } from "~/components/floor-plan/map-view";
import { SectionButtons } from "~/components/floor-plan/section-buttons";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { RegisterButton } from "~/components/ui/register-button";
import { useTable } from "~/hooks/use-table";
import { cn } from "~/lib/utils";
import { useTableManager } from "~/managers/use-table-manager";

export const Route = createFileRoute("/_main/settings/_layout/floor-plan")({
  component: FloorPlan,
});

const GRID_SIZE = 10;

function snapToGrid(x: number, y: number) {
  const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
  const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
  return { x: snappedX, y: snappedY };
}

function Droppable({ children }: { children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({
    id: "droppable",
  });

  return <div ref={setNodeRef}>{children}</div>;
}

const Draggable = forwardRef<
  HTMLDivElement,
  {
    table: Table;
    styles: React.CSSProperties;
    transform?: { x: number; y: number };
    children: React.ReactNode;
  }
>(({ table, styles, transform, children }, ref) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: table.id,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : {};

  return (
    <div
      style={{ ...style, ...styles }}
      ref={(node) => {
        setNodeRef(node);
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
});

function FloorPlan() {
  const { tables } = useTable();
  const containerRef = useRef<HTMLDivElement>(null);
  const [tempTables, setTempTables] = useState<Table[]>([]);
  const [activeId, setActiveId] = useState<UniqueIdentifier>();
  const [canPan, setCanPan] = useState(true);
  const [floorPlanContextOpen, setFloorPlanContextOpen] = useState(false);
  const { saveLayout } = useTableManager();
  const [selectedItemId, setSelectedItemId] = useState<
    UniqueIdentifier | undefined
  >();

  const selectedTable = selectedItemId
    ? tempTables.find((table) => table.id === selectedItemId)
    : undefined;

  // Ref map to store each table's DOM element
  const tableRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 100,
      distance: 5,
      tolerance: 100,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      distance: 5,
      tolerance: 0,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const [dragTransforms, setDragTransforms] = useState<
    Record<string, { x: number; y: number }>
  >({});

  function handleDragStart(ev: DragStartEvent) {
    setActiveId(ev.active.id);
    const table = tempTables.find((x) => x.id === ev.active.id);
    setSelectedItemId(table?.id);
  }

  function handleDragMove(ev: DragMoveEvent) {
    if (ev.active.id) {
      const snappedPosition = snapToGrid(ev.delta.x, ev.delta.y);

      setDragTransforms((prev) => ({
        ...prev,
        [ev.active.id]: {
          x: snappedPosition.x,
          y: snappedPosition.y,
        },
      }));
    }
  }

  function handleDragEnd(ev: DragEndEvent) {
    if (!ev.active.id) return;

    // Retrieve the last snapped position from dragTransforms
    const dragTransform = dragTransforms[ev.active.id];

    // Apply the final snapped position directly to the table
    handleMove(ev.active.id, dragTransform.x, dragTransform.y);

    // Reset the transform since the drag has ended
    setDragTransforms((prev) => ({
      ...prev,
      [ev.active.id]: { x: 0, y: 0 }, // Reset the transform after dragging ends
    }));

    setActiveId(undefined);
  }

  function handleRotate(id: UniqueIdentifier, rotate: number) {
    const tableIndex = tempTables.findIndex((table) => table.id === id);

    if (tableIndex === -1) {
      return;
    }

    const updatedTable = { ...tempTables[tableIndex] };

    const newRotation = updatedTable.rotation + rotate;

    updatedTable.rotation = newRotation;

    const updatedTables = [...tempTables];
    updatedTables[tableIndex] = updatedTable;

    setTempTables(updatedTables);
  }

  function handleMove(id: UniqueIdentifier, x: number, y: number) {
    const tableIndex = tempTables.findIndex((table) => table.id === id);

    if (tableIndex === -1) {
      return;
    }

    const updatedTable = { ...tempTables[tableIndex] };

    let newX = updatedTable.posX + x;
    let newY = updatedTable.posY + y;

    // Access the DOM element using the ref
    const draggableElement = tableRefs.current[id as string];

    if (draggableElement) {
      const { width, height } = draggableElement.getBoundingClientRect();

      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();

        // Adjust positions to stay within container bounds using actual dimensions
        const minX = 0;
        const minY = 0;
        const maxX = containerRect.width - width;
        const maxY = containerRect.height - height;

        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));
      }
    }

    // Update the table's position
    updatedTable.posX = newX;
    updatedTable.posY = newY;

    // Update the tempTables state
    const updatedTables = [...tempTables];
    updatedTables[tableIndex] = updatedTable;

    // Reset the drag transform
    setDragTransforms((prev) => ({
      ...prev,
      [id]: { x: 0, y: 0 },
    }));

    setTempTables(updatedTables);
  }

  useEffect(() => {
    setTempTables(tables.map((table) => ({ ...table })));
  }, [tables]);

  // Cleanup refs on unmount
  useEffect(() => {
    return () => {
      tableRefs.current = {};
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 overflow-auto">
      <div className="h-12">
        <RegisterButton
          disabled={tempTables.length === 0}
          variant="success"
          onClick={() => {
            saveLayout(tempTables);
          }}
        >
          Save Layout
        </RegisterButton>
      </div>
      <div className="flex flex-row gap-2 items-center">
        <div className="py-2 flex flex-row items-center gap-2">
          <Button
            onClick={() => {
              if (!selectedItemId) return;
              handleMove(selectedItemId, -GRID_SIZE, 0);
            }}
            disabled={!selectedItemId}
          >
            <ArrowLeftIcon />
          </Button>
          <Button
            onClick={() => {
              if (!selectedItemId) return;
              handleMove(selectedItemId, GRID_SIZE, 0);
            }}
            disabled={!selectedItemId}
          >
            <ArrowRightIcon />
          </Button>
          <Button
            onClick={() => {
              if (!selectedItemId) return;
              handleMove(selectedItemId, 0, -GRID_SIZE);
            }}
            disabled={!selectedItemId}
          >
            <ArrowUpwardIcon />
          </Button>
          <Button
            onClick={() => {
              if (!selectedItemId) return;
              handleMove(selectedItemId, 0, GRID_SIZE);
            }}
            disabled={!selectedItemId}
          >
            <ArrowDownwardIcon />
          </Button>
          <Button
            onClick={() => {
              if (!selectedItemId) return;
              handleRotate(selectedItemId, -15);
            }}
            disabled={!selectedItemId}
          >
            <RotateLeftIcon />
          </Button>
          <Button
            onClick={() => {
              if (!selectedItemId) return;
              handleRotate(selectedItemId, 15);
            }}
            disabled={!selectedItemId}
          >
            <RotateRightIcon />
          </Button>
        </div>
        <div className="flex flex-col text-sm">
          {selectedTable && <span>PosX: {selectedTable.posX}</span>}
          {selectedTable && <span>PosY: {selectedTable.posY}</span>}
        </div>
      </div>
      <div>
        <SectionButtons />
      </div>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToParentElement]}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        <Droppable>
          {/* <CustomFloorPlan>
          {tempTables.map((table) => (
              <Draggable
                key={table.id}
                ref={(el) => {
                  tableRefs.current[table.id] = el;
                }}
                styles={{
                  position: "absolute",
                  left: `${table.posX}px`, // No zoom applied here
                  top: `${table.posY}px`, // No zoom applied here
                }}
                table={table}
                transform={dragTransforms[table.id]}
              >
                <FloorPlanItem
                  className={cn(selectedItemId === table.id && "fill-blue-200")}
                  onMouseDown={() => {
                    setCanPan(false);
                  }}
                  onMouseUp={() => {
                    setCanPan(true);
                  }}
                  onMouseLeave={() => {
                    setCanPan(true);
                  }}
                  onClick={() => {
                    setSelectedItemId(table.id);
                  }}
                  key={table.id}
                  table={table}
                />
              </Draggable>
            ))}
          </CustomFloorPlan> */}
          <FloorPlanMapView
            canZoom={false}
            ref={containerRef}
            onClick={() => {
              setFloorPlanContextOpen(true);
            }}
            canPan={canPan && !activeId}
          >
            {tempTables.map((table) => (
              <Draggable
                key={table.id}
                ref={(el) => {
                  tableRefs.current[table.id] = el;
                }}
                styles={{
                  position: "absolute",
                  left: `${table.posX}px`, // No zoom applied here
                  top: `${table.posY}px`, // No zoom applied here
                }}
                table={table}
                transform={dragTransforms[table.id]}
              >
                <FloorPlanItem
                  className={cn(selectedItemId === table.id && "fill-blue-200")}
                  onMouseDown={() => {
                    setCanPan(false);
                  }}
                  onMouseUp={() => {
                    setCanPan(true);
                  }}
                  onMouseLeave={() => {
                    setCanPan(true);
                  }}
                  onClick={() => {
                    setSelectedItemId(table.id);
                  }}
                  key={table.id}
                  table={table}
                />
              </Draggable>
            ))}
          </FloorPlanMapView>
        </Droppable>
      </DndContext>
      <Dialog
        open={floorPlanContextOpen}
        onOpenChange={setFloorPlanContextOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add item</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <div>woel</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FloorPlan;
